import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import Pager from "./Pager";
import useInstituteData, { percentOf, isPass, fmtDate, downloadCsv } from "../Useinstitutedata";
import { useInstitute } from "../context/InstituteContext";
import {
  addInstituteStudent,
  bulkAddInstituteStudents,
  deleteStudent,
} from "../api/instituteclient";
import { extractPdfLines, parseStudentLines, validateStudent } from "../Pdfstudent";
import InstituteTopbar from "./Institutetopbar";
import InstituteSidebar from "./Institutesidebar";

const COURSE_OPTIONS = ["CMA USA", "ACCA", "EA"]; // same list as student Signup
const PAGE_SIZE = 8;

const newForm = () => ({
  fullName: "",
  email: "",
  mobile: "",
  gender: "",
  courses: [],
});
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function InstituteStudents() {
  const location = useLocation();
  const { currentInstitute } = useInstitute();
  const { students, purchases, scores, loading, error, setError, reload } = useInstituteData();

  // ---- list state ----
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);

  // ---- manual add ----
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(newForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // ---- PDF import ----
  const fileRef = useRef(null);
  const [showImport, setShowImport] = useState(false);
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [rows, setRows] = useState([]);
  const [importCourses, setImportCourses] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");

  const openAdd = () => {
    setForm(newForm());
    setFormError("");
    setShowAdd(true);
  };

  const openImport = () => {
    setStep("upload");
    setRows([]);
    setResult(null);
    setImportError("");
    setFileName("");
    setImportCourses([]);
    setShowImport(true);
  };

  // Dashboard buttons deep-link here with { state: { open: "add" | "import" } }.
  useEffect(() => {
    if (location.state?.open === "add") openAdd();
    if (location.state?.open === "import") openImport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setPage(1), [search, statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      const matches =
        !q ||
        s.fullName?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.mobile?.includes(q);
      return matches && (statusFilter === "All" || (s.status || "Active") === statusFilter);
    });
  }, [students, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ---------------- manual add ---------------- */
  const saveStudent = async () => {
  setFormError("");

  const err = validateStudent(form);
  if (err) {
    setFormError(err);
    return;
  }

  if (form.courses.length === 0) {
    setFormError("Select at least one course.");
    return;
  }

  setSaving(true);

  try {
    await addInstituteStudent({
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
      gender: form.gender,
      courses: form.courses,
    });

    setShowAdd(false);
    setForm(newForm());
    await reload();
  } catch (e) {
    setFormError(
      e.response?.data?.detail ||
      e.message ||
      "Could not add student."
    );
  } finally {
    setSaving(false);
  }
};

  const handleDelete = async (s) => {
    if (!window.confirm(`Remove ${s.fullName}? This cannot be undone.`)) return;
    try {
      await deleteStudent(s.id);
      reload();
    } catch {
      setError("Could not delete student.");
    }
  };

  /* ---------------- PDF import ---------------- */
  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) return setImportError("Please choose a PDF file.");
    setImportError("");
    setParsing(true);
    setFileName(file.name);
    try {
      const parsed = parseStudentLines(await extractPdfLines(file));
      if (parsed.length === 0) {
        setImportError("No student rows found. Each student needs at least a name and an email on one line.");
      } else {
        setRows(parsed.map((r, i) => ({ ...r, key: i })));
        setStep("preview");
      }
    } catch {
      setImportError("Could not read this PDF. Scanned (image-only) PDFs have no text to extract.");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Validate every row and flag duplicates inside the file itself.
  const checked = useMemo(() => {
    const seen = new Set();
    return rows.map((r) => {
      const k = r.email.trim().toLowerCase();
      let error = validateStudent(r);
      if (!error && seen.has(k)) error = "Duplicate email in file.";
      seen.add(k);
      return { ...r, error };
    });
  }, [rows]);

  const validRows = checked.filter((r) => !r.error);
  const needsCourse = validRows.some((r) => r.courses.length === 0);

  const runImport = async () => {
    setImportError("");
    if (validRows.length === 0) return setImportError("There are no valid rows to import.");
    if (needsCourse && importCourses.length === 0)
      return setImportError("Pick a default course for rows that don't list one.");

    setImporting(true);
    try {
      
      const res = await bulkAddInstituteStudents(
  validRows.map((r) => ({
    fullName: r.fullName.trim(),
    email: r.email.trim(),
    mobile: r.mobile.trim(),
    gender: r.gender || "Other",
    courses: r.courses.length ? r.courses : importCourses,
  }))
);

setResult(res.data ?? res);
setStep("done");
await reload();
    } catch {
      setImportError("Import failed. Please check the Django API response and try again.");
    } finally {
      setImporting(false);
    }
  };

  

  /* ---------------- detail modal data ---------------- */
  const viewedPurchases = viewing ? purchases.filter((p) => String(p.userId) === String(viewing.id)) : [];
  const viewedScores = viewing ? scores.filter((s) => String(s.userId) === String(viewing.id)) : [];

  return (
    <div className="admin-page-full d-flex">
      <InstituteSidebar />
      <div className="admin-content">
        <InstituteTopbar />

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h2 className="h5 fw-bold mb-0">Students</h2>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={openImport}>
              <i className="bi bi-file-earmark-pdf" /> Import from PDF
            </button>
            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={openAdd}>
              <i className="bi bi-person-plus" /> Add Student
            </button>
          </div>
        </div>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="admin-card p-3 mb-3">
          <div className="row g-2">
            <div className="col-12 col-md-7">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                <input className="form-control" placeholder="Search by name, email or mobile..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="col-12 col-md-5">
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="All">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="admin-card p-3 p-md-4">
          {loading && <p className="text-muted mb-0">Loading students...</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-muted small mb-0">
              {students.length === 0 ? "You haven't registered any students yet." : "No students match your filters."}
            </p>
          )}

          {!loading && filtered.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>Courses</th><th>Status</th><th>Registered</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((s, i) => {
                      const status = s.status || "Active";
                      return (
                        <tr key={s.id}>
                          <td className="text-muted">{(page - 1) * PAGE_SIZE + i + 1}</td>
                          <td className="fw-semibold">{s.fullName}</td>
                          <td className="text-muted">{s.email}</td>
                          <td className="text-muted">{s.mobile || "—"}</td>
                          <td>{(Array.isArray(s.courses) ? s.courses : []).join(", ") || "—"}</td>
                          <td><span className={`admin-status-pill ${status.toLowerCase()}`}>{status}</span></td>
                          <td className="text-muted small">{fmtDate(s.registeredAt)}</td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-secondary me-1" title="View" onClick={() => setViewing(s)}>
                              <i className="bi bi-eye" />
                            </button>
                            <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => handleDelete(s)}>
                              <i className="bi bi-trash" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} />
            </>
          )}
        </div>
      </div>

      {/* ---------------- Add student modal ---------------- */}
      {showAdd && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Student</h5>
                <button className="btn-close" onClick={() => setShowAdd(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small text-muted">Full Name</label>
                  <input className="form-control" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small text-muted">Email</label>
                    <input type="email" className="form-control" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">Mobile</label>
                    <input className="form-control" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/[^\d+\s-]/g, "") }))} />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small text-muted">Gender</label>
                  <select className="form-select" value={form.gender} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}>
                    <option value="">Select</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="col-12 mb-3">
                  <div className="alert alert-info small mb-0">
                    A temporary password will be generated by the server and sent to the student's email address.
                  </div>
                </div>
                <div className="mb-1">
                  <label className="form-label small text-muted d-block">Courses</label>
                  {COURSE_OPTIONS.map((c) => (
                    <div className="form-check form-check-inline" key={c}>
                      <input className="form-check-input" type="checkbox" id={`add-${c}`} checked={form.courses.includes(c)} onChange={() => setForm((f) => ({ ...f, courses: toggle(f.courses, c) }))} />
                      <label className="form-check-label small" htmlFor={`add-${c}`}>{c}</label>
                    </div>
                  ))}
                </div>
                {formError && <div className="text-danger small mt-2">{formError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveStudent} disabled={saving}>{saving ? "Saving..." : "Add Student"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- PDF import modal ---------------- */}
      {showImport && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)", overflowY: "auto" }}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Students from PDF</h5>
                <button className="btn-close" onClick={() => !importing && setShowImport(false)} />
              </div>

              <div className="modal-body">
                {step === "upload" && (
                  <>
                    <div
                      className="inst-dropzone"
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleFile(e.dataTransfer.files?.[0]);
                      }}
                    >
                      <i className="bi bi-file-earmark-pdf" />
                      <div className="fw-semibold">{parsing ? `Reading ${fileName}...` : "Click to choose a PDF, or drop it here"}</div>
                      <div className="text-muted small">One student per line or table row, with at least a name and an email.</div>
                      <input ref={fileRef} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
                    </div>
                    <div className="inst-format mt-3">
                      <div className="small fw-semibold mb-1">Example row</div>
                      <code>1&nbsp;&nbsp;Anu Mary&nbsp;&nbsp;anu@example.com&nbsp;&nbsp;9876543210&nbsp;&nbsp;CMA USA</code>
                      <div className="small text-muted mt-1">Mobile and course are optional. Login passwords are generated for you after import.</div>
                    </div>
                  </>
                )}

                {step === "preview" && (
                  <>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                      <div className="small text-muted">
                        <strong>{fileName}</strong> — {validRows.length} valid of {checked.length} rows found. Fix or remove any flagged rows.
                      </div>
                      {needsCourse && (
                        <div className="small">
                          <span className="text-muted me-2">Default course for rows without one:</span>
                          {COURSE_OPTIONS.map((c) => (
                            <div className="form-check form-check-inline me-2" key={c}>
                              <input className="form-check-input" type="checkbox" id={`imp-${c}`} checked={importCourses.includes(c)} onChange={() => setImportCourses((l) => toggle(l, c))} />
                              <label className="form-check-label" htmlFor={`imp-${c}`}>{c}</label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="table-responsive" style={{ maxHeight: 380 }}>
                      <table className="table align-middle admin-table mb-0">
                        <thead><tr><th>#</th><th>Name</th><th>Email</th><th>Mobile</th><th>Course</th><th>Check</th><th /></tr></thead>
                        <tbody>
                          {checked.map((r, i) => (
                            <tr key={r.key} className={r.error ? "table-danger" : ""}>
                              <td className="text-muted">{i + 1}</td>
                              <td><input className="form-control form-control-sm inst-cell" value={r.fullName} onChange={(e) => updateRow(r.key, { fullName: e.target.value })} /></td>
                              <td><input className="form-control form-control-sm inst-cell" value={r.email} onChange={(e) => updateRow(r.key, { email: e.target.value })} /></td>
                              <td><input className="form-control form-control-sm inst-cell" value={r.mobile} onChange={(e) => updateRow(r.key, { mobile: e.target.value })} /></td>
                              <td className="small">{r.courses.join(", ") || <span className="text-muted">default</span>}</td>
                              <td className="small">
                                {r.error ? <span className="text-danger">{r.error}</span> : <span className="admin-status-pill active">OK</span>}
                              </td>
                              <td className="text-end">
                                <button className="btn btn-sm btn-outline-danger" title="Remove row" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                                  <i className="bi bi-x-lg" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {step === "done" && result && (
                  <>
                    <div className="admin-login-success mb-3">
                      {result.added.length} student{result.added.length === 1 ? "" : "s"} registered
                      {result.skipped.length > 0 && `, ${result.skipped.length} skipped`}.
                    </div>
                    {result.added.length > 0 && (
  <p className="small text-muted mb-2">
    Temporary passwords are generated by the server and sent to each
    student's email address.
  </p>
)}
                    {result.skipped.length > 0 && (
                      <div className="table-responsive mt-2">
                        <table className="table admin-table align-middle mb-0">
                          <thead><tr><th>Name</th><th>Email</th><th>Reason</th></tr></thead>
                          <tbody>
                            {result.skipped.map((s, i) => (
                              <tr key={i}><td>{s.fullName}</td><td className="text-muted">{s.email}</td><td className="text-danger small">{s.reason}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {importError && <div className="text-danger small mt-3">{importError}</div>}
              </div>

              <div className="modal-footer">
                {step === "preview" && (
                  <>
                    <button className="btn btn-outline-secondary" onClick={() => { setStep("upload"); setRows([]); }} disabled={importing}>Choose another PDF</button>
                    <button className="btn btn-primary" onClick={runImport} disabled={importing || validRows.length === 0}>
                      {importing ? "Importing..." : `Import ${validRows.length} student${validRows.length === 1 ? "" : "s"}`}
                    </button>
                  </>
                )}
                {step !== "preview" && (
                  <button className="btn btn-outline-secondary" onClick={() => setShowImport(false)}>{step === "done" ? "Close" : "Cancel"}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Student detail modal ---------------- */}
      {viewing && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)", overflowY: "auto" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{viewing.fullName}</h5>
                <button className="btn-close" onClick={() => setViewing(null)} />
              </div>
              <div className="modal-body">
                <table className="table table-borderless mb-3">
                  <tbody>
                    <tr><td className="text-muted small">Email</td><td>: {viewing.email}</td></tr>
                    <tr><td className="text-muted small">Mobile</td><td>: {viewing.mobile || "—"}</td></tr>
                    <tr><td className="text-muted small">Courses</td><td>: {(viewing.courses || []).join(", ") || "—"}</td></tr>
                    <tr><td className="text-muted small">Registered</td><td>: {fmtDate(viewing.registeredAt)}</td></tr>
                  </tbody>
                </table>

                <h6 className="fw-bold">Exams booked</h6>
                {viewedPurchases.length === 0 ? (
                  <p className="text-muted small">No exams booked yet.</p>
                ) : (
                  <div className="table-responsive mb-3">
                    <table className="table admin-table mb-0">
                      <thead><tr><th>Course</th><th>Exam date</th><th>Fee</th></tr></thead>
                      <tbody>
                        {viewedPurchases.map((p) => (
                          <tr key={p.id}><td>{p.courseName}</td><td>{fmtDate(p.examDate)}</td><td>₹{Number(p.fee).toLocaleString("en-IN")}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h6 className="fw-bold">Marks</h6>
                {viewedScores.length === 0 ? (
                  <p className="text-muted small mb-0">No exam attempted yet.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table admin-table mb-0">
                      <thead><tr><th>Course</th><th>Score</th><th>Result</th></tr></thead>
                      <tbody>
                        {viewedScores.map((r) => (
                          <tr key={r.id}>
                            <td>{r.courseName}</td>
                            <td>{r.score}/{r.total} ({percentOf(r)}%)</td>
                            <td><span className={`admin-status-pill ${isPass(r.result) ? "active" : "inactive"}`}>{r.result}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setViewing(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}